import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Headphones, Loader2, Mail, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { getApiErrorMessage } from '../../lib/apiError';
import AdminUserDetailModal from '../../components/admin/AdminUserDetailModal';

const CustomerCareAgentsListPage = () => {
  const { authApi } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sendingSetupId, setSendingSetupId] = useState('');
  const [detailUserId, setDetailUserId] = useState('');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const response = await authApi.get('/admin/customer-care-agents');
        if (mounted) setAgents(response.data.items || []);
      } catch (err) {
        if (mounted) setError(getApiErrorMessage(err, 'Unable to load customer care agents.'));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [authApi]);

  const handleSendSetup = async (agent) => {
    const agentId = agent.id || agent._id;
    if (!agentId) return;
    setSendingSetupId(agentId);
    try {
      const response = await authApi.post(`/admin/customer-care-agents/${agentId}/send-setup`);
      toast.success(response.data?.message || `Setup email sent to ${agent.email}`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Unable to send setup email.'));
    } finally {
      setSendingSetupId('');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-serif text-3xl font-medium text-[#1A1A1A]">Customer Care Agents</h1>
          <p className="mt-1 text-sm text-[#5A5A5A]">Admin-managed support operations staff.</p>
        </div>
        <Button asChild>
          <Link to="/admin/customer-care-agents/create">
            <Plus className="h-4 w-4" />
            Create Agent
          </Link>
        </Button>
      </div>

      {error && <div className="mb-5 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="rounded-lg border border-[#E8E6E3] bg-white">
        {loading ? (
          <div className="flex items-center justify-center p-12 text-[#5A5A5A]">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Loading agents...
          </div>
        ) : agents.length === 0 ? (
          <div className="p-12 text-center">
            <Headphones className="mx-auto mb-4 h-12 w-12 text-gray-300" />
            <p className="text-sm text-[#5A5A5A]">No customer care agents created yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-[#E8E6E3] bg-[#FAFAF7] text-left text-xs uppercase tracking-wide text-[#5A5A5A]">
                <tr>
                  <th className="px-5 py-4">Name</th>
                  <th className="px-5 py-4">Email</th>
                  <th className="px-5 py-4">Phone</th>
                  <th className="px-5 py-4">Qualification</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E6E3]">
                {agents.map(agent => {
                  const userId = agent.userId || agent.user_id;
                  const agentId = agent.id || agent._id;
                  const firstName = agent.firstName || agent.first_name || '';
                  const lastName = agent.lastName || agent.last_name || '';
                  const name = [firstName, lastName].filter(Boolean).join(' ') || agent.email || 'Customer care agent';
                  return (
                    <tr key={agentId || agent.email}>
                      <td className="px-5 py-4 font-medium text-[#1A1A1A]">
                        <button
                          type="button"
                          onClick={() => userId && setDetailUserId(userId)}
                          disabled={!userId}
                          className="text-left font-medium text-[#1A1A1A] hover:text-[#9F1D35] disabled:cursor-default disabled:hover:text-[#1A1A1A]"
                        >
                          {name}
                        </button>
                      </td>
                      <td className="px-5 py-4 text-sm text-[#5A5A5A]">{agent.email}</td>
                      <td className="px-5 py-4 text-sm text-[#5A5A5A]">{agent.primaryPhone || agent.phone || '-'}</td>
                      <td className="px-5 py-4 text-sm text-[#5A5A5A]">{agent.highestQualification || '-'}</td>
                      <td className="px-5 py-4">
                        <Badge className="border border-green-200 bg-green-100 text-green-700">{agent.status || 'active'}</Badge>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleSendSetup(agent)}
                          disabled={sendingSetupId === agentId}
                        >
                          {sendingSetupId === agentId ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Mail className="mr-2 h-4 w-4" />
                          )}
                          Send setup email
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <AdminUserDetailModal
        userId={detailUserId}
        open={Boolean(detailUserId)}
        onOpenChange={(open) => !open && setDetailUserId('')}
      />
    </div>
  );
};

export default CustomerCareAgentsListPage;
